import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';

import { HOTEL_STATUSES } from 'constants/booking';
import { codes } from 'constants/payment';
import { findJacTravelHotel, getHotelsStatuses } from 'utils/booking';
import { isUserEurostarAgent, isUserStationAgent } from 'utils/user';
import { sum } from 'utils/calculator';
import { GET_CANCEL_HOTEL_REFUND_PROPOSAL, GET_HOTEL_BOOKINGS_INFO, GET_HOTEL_REFUND_PROPOSAL, HOTEL_BOOKING } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { historyShape, userShape, paymentsShape } from 'constants/propTypes';
import AgentPortalError from 'errors/AgentPortalError';
import { bookingStatus } from 'types/booking';
import { getExchangePaymentURL, getRefundMethodsURL } from 'utils/urls';

import { CANCEL_HOTEL_BOOKING, CANCEL_HOTEL_PRODUCT, CANCEL_HOTEL_JACTRAVEL } from 'store/mutations';
import { buildHotelInfoFromDotwBooking, buildRefundProposal, buildHotelBookingPenalties, isHotelBookingCanceled } from 'utils/hotel';
import { Column } from 'components/shared/Layout';

import { EXCHANGE_HOTEL_PRODUCT } from './graphql';
import ManageHotel from './ManageHotel';

const ManageHotelContainer = ({
  user,
  canBeCancelledOnThirdParty,
  history: { push },
  date,
  validityEndDate,
  payments = [],
  jacTravelRef,
  hotelId,
  itemRef,
  priceWithMargin,
  bookingReference,
  currency,
  isConfirmed,
  isPackage,
  isHotelOnly,
  margin,
  isCompensated,
  isDotwBooking,
  canExchange
}) => {
  const { data: hotelsData } = useQuery(isDotwBooking ? HOTEL_BOOKING : GET_HOTEL_BOOKINGS_INFO, {
    variables: isDotwBooking ? { reference: bookingReference } : { bookingReference }
  });

  const isAlreadyCancelled = isHotelBookingCanceled(hotelsData);
  const penaltiesApplied = buildHotelBookingPenalties(hotelsData);

  const [cancelHotelProduct, cancelHotelProductState] = useMutation(CANCEL_HOTEL_PRODUCT, {
    onCompleted: ({ cancelHotelProduct: { paymentsRefundBalance } }) => {
      if (paymentsRefundBalance < 0) {
        push(getRefundMethodsURL(bookingReference, currency));
      }
    },
    fetchPolicy: 'no-cache',
    refetchQueries: ['booking', isDotwBooking ? 'hotelBooking' : 'getHotelBookings'],
    awaitRefetchQueries: true
  });

  const [exchangeHotelProduct, exchangeHotelProductState] = useMutation(EXCHANGE_HOTEL_PRODUCT, {
    onCompleted: ({ exchangeHotelProduct: { totalPriceToBePaid, status } }) => {
      if (totalPriceToBePaid < 0 || status === bookingStatus.PENDING_REFUND) {
        push(getRefundMethodsURL(bookingReference, currency));
      }

      if (totalPriceToBePaid > 0) {
        push(getExchangePaymentURL(bookingReference, currency));
      }
    },
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const refundProposal = useQuery(isDotwBooking ? GET_CANCEL_HOTEL_REFUND_PROPOSAL : GET_HOTEL_REFUND_PROPOSAL, {
    variables: isDotwBooking ? { reference: bookingReference } : { bookingReference },
    skip: !canBeCancelledOnThirdParty || isAlreadyCancelled
  });

  const { data, error: refundProposalError } = refundProposal;

  const refundProposalData = useMemo(() => {
    if (!isDotwBooking) {
      return data && data.refundProposal;
    } else {
      return buildRefundProposal(data);
    }
  }, [data, isDotwBooking]);

  const [cancelHotelJacTravel] = useMutation(isDotwBooking ? CANCEL_HOTEL_BOOKING : CANCEL_HOTEL_JACTRAVEL, {
    variables: isDotwBooking
      ? { options: { reference: bookingReference } }
      : {
          bookingReference
        }
  });

  const totalCardPaymentsAmount = sum(payments.filter((p) => p.method === codes.CARD && p.isSuccessful).map((p) => p.amount)) || 0;
  const isHotelInThePast = moment(validityEndDate).isBefore(moment.now());
  const isCompensationDisabled = totalCardPaymentsAmount < priceWithMargin || !isHotelInThePast;

  const state = [cancelHotelProductState, exchangeHotelProductState, refundProposal];
  const error = state.map((item) => item.error).find(Boolean);
  const loading = state.some((item) => item.loading);

  const [cancelHotelJacTravelError, setCancelHotelJacTravelError] = useState(false);

  const jacTravelHotel = isDotwBooking
    ? buildHotelInfoFromDotwBooking(hotelsData?.hotelBooking)
    : hotelsData?.getHotelBookings && findJacTravelHotel(jacTravelRef, hotelsData.getHotelBookings);

  const hotelStatuses =
    (hotelsData?.getHotelBookings || hotelsData?.hotelBooking) &&
    getHotelsStatuses([{ jacTravelRef }], isDotwBooking ? hotelsData?.hotelBooking : hotelsData.getHotelBookings, isDotwBooking);

  const jacTravelTotalPrice = jacTravelHotel?.totalPrice;
  const jacTravelTotalCommission = jacTravelHotel?.totalCommission;

  const isHotelCancelledOrNotFoundInJacTravel =
    hotelStatuses?.length &&
    hotelStatuses.find((h) => h.jacTravelStatus === HOTEL_STATUSES.CANCELLED || h.jacTravelStatus === HOTEL_STATUSES.NOT_FOUND);

  const handleCancel = async ({ fee, marginFee }) => {
    try {
      if (canBeCancelledOnThirdParty && !isHotelCancelledOrNotFoundInJacTravel && !isAlreadyCancelled) {
        try {
          const result = await cancelHotelJacTravel();

          if (!isDotwBooking && !result.data.refund.success) {
            return setCancelHotelJacTravelError(true);
          }
        } catch (err) {
          return setCancelHotelJacTravelError(true);
        }
      }

      await cancelHotelProduct({
        variables: {
          bookingReference,
          fee,
          marginFee,
          itemRef
        }
      });
    } catch (error) {
      console.log('Error', error);
    }
  };

  const handleExchange = async (formData) => {
    await exchangeHotelProduct({
      variables: {
        bookingReference,
        oldHotelInput: { itemRef },
        newHotelInput: {
          fee: formData.fee,
          ...(formData.margin && { margin: formData.margin }),
          price: formData.price,
          hotelId: formData.hotelId,
          checkInDate: formData.checkInDate,
          jacTravelRef: formData.jacTravelRef
        }
      }
    });
  };

  if (cancelHotelJacTravelError) {
    throw new Error('Sorry, we were unable to cancel this booking on a third party provider at the moment, please try again later');
  }
  if (error) {
    throw new AgentPortalError(error);
  }
  if (loading) {
    return (
      <Column alignItems="center" justifyContent="center">
        <Spinner />
      </Column>
    );
  }

  return (
    <ManageHotel
      canBeCancelledOnThirdParty={canBeCancelledOnThirdParty}
      currency={currency}
      date={date}
      hotelId={hotelId}
      isConfirmed={isConfirmed}
      jacTravelRef={jacTravelRef}
      loading={loading}
      price={priceWithMargin}
      jacTravelTotalPrice={jacTravelTotalPrice}
      jacTravelTotalCommission={jacTravelTotalCommission}
      onCancel={handleCancel}
      onExchange={handleExchange}
      isPackage={isPackage}
      isHotelOnly={isHotelOnly}
      refundProposal={refundProposalData}
      refundProposalError={!!refundProposalError}
      margin={margin}
      canCompensate={isUserEurostarAgent(user) && !isUserStationAgent(user)}
      isCompensationDisabled={isCompensationDisabled}
      isCompensated={isCompensated}
      isDotwBooking={isDotwBooking}
      isAlreadyCancelled={isAlreadyCancelled}
      penaltiesApplied={penaltiesApplied}
      canExchange={canExchange}
    />
  );
};

ManageHotelContainer.propTypes = {
  user: userShape,
  canBeCancelledOnThirdParty: PropTypes.bool,
  bookingReference: PropTypes.string.isRequired,
  itemRef: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  validityEndDate: PropTypes.string,
  jacTravelRef: PropTypes.string.isRequired,
  hotelId: PropTypes.string.isRequired,
  priceWithMargin: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  isConfirmed: PropTypes.bool.isRequired,
  isPackage: PropTypes.bool.isRequired,
  isHotelOnly: PropTypes.bool,
  margin: PropTypes.number,
  history: historyShape,
  payments: paymentsShape,
  isCompensated: PropTypes.bool,
  isDotwBooking: PropTypes.bool,
  canExchange: PropTypes.bool
};

export default ManageHotelContainer;
